import { Theme } from "@fluentui/react-components";
import { setAskPrincipal, setTelemetrySink } from "ask/ask-client";
import { HelpLauncher, HelpNotice, UserManualFile } from "help/HelpLauncher";
import { helpLanguageForLocale } from "help/language";
import type { HelpLanguage } from "help/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppStore } from "store/AppStore";
import { appInsights } from "../insights";

// The Ask tab answers in the browser; only its two product events leave the page — to the same
// Application Insights resource as the app's own telemetry (the miss log is
// `customEvents | where name == "AssistantInteraction" and customDimensions.matchFound == "false"`).
setTelemetrySink({
  track: (name, properties, measurements) =>
    appInsights.trackEvent({
      name,
      properties: properties as Record<string, unknown>,
      measurements,
    }),
});

/**
 * The docked "?" sits left of the notification bell. GrecoHeader (@greco/components) renders its
 * icons — bell, settings — in `.middle-content__icons` and takes no children, so an empty slot is
 * inserted as that container's FIRST child and the launcher portals into it. `display: contents`
 * makes the button itself the flex item, exactly like the header's own icon buttons.
 *
 * Kept first by an observer: the header may re-render or mount late (after sign-in). The check
 * that runs on every DOM change is two property reads; the query only runs when the slot is not
 * where it belongs.
 */
const HEADER_ICONS = ".greco-header__middle .middle-content__icons";

const useHeaderHelpSlot = (): HTMLElement | null => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const el = document.createElement("span");
    el.className = "pct-help-header-slot";
    el.style.display = "contents";
    const place = () => {
      if (el.isConnected && el.parentElement?.firstChild === el) return;
      const icons = document.querySelector(HEADER_ICONS);
      if (!icons) {
        setSlot(null); // no header (yet) → the floating "?" is the fallback
        return;
      }
      icons.insertBefore(el, icons.firstChild);
      setSlot(el);
    };
    place();
    const observer = new MutationObserver(place);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      el.remove();
    };
  }, []);
  return slot;
};

/** The user manual PDF (in `public/manuals`) for a guide language: PCT-User-Manual-EN/LT/LV.pdf. */
export const userManualFor = (language: HelpLanguage): UserManualFile => {
  const fileName = `PCT-User-Manual-${language.toUpperCase()}.pdf`;
  return { href: `${process.env.PUBLIC_URL}/manuals/${fileName}`, fileName };
};

const notify = ({ severity, message }: HelpNotice) => {
  if (severity === "warning") toast.warning(message);
  else if (severity === "success") toast.success(message);
  else toast.info(message);
};

/**
 * Shell adapter for the in-app page guide: the only place it touches the router, i18next and
 * the app store, so everything under `help/` stays importable with no providers around it.
 */
const AppHelp = ({ theme }: { theme: Theme }) => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [{ appState }] = useAppStore();
  const userId: string | undefined = appState.user?.id;
  const dockSlot = useHeaderHelpSlot();

  // The id only collapses one person's keystroke burst into one miss-log row.
  useEffect(() => {
    setAskPrincipal(userId ? { id: userId, roles: [] } : null);
  }, [userId]);

  return (
    <HelpLauncher
      pathname={pathname}
      theme={theme}
      appLanguage={helpLanguageForLocale(i18n.language)}
      notify={notify}
      dockSlot={dockSlot}
      userManualFor={userManualFor}
    />
  );
};

export default AppHelp;
